import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiShield } = FiIcons;

const FullScreenLoader = () => (
  <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white">
    <SafeIcon icon={FiLoader} className="animate-spin text-blue-500 text-5xl mb-4" />
    <p className="text-gray-400">Checking permissions...</p>
  </div>
);

const AdminDeniedScreen = ({ userEmail, userRole, isAdmin }) => (
  <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white p-8">
    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 max-w-md text-center">
      <SafeIcon icon={FiShield} className="text-red-400 text-6xl mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-red-300 mb-4">Access Denied</h1>
      <p className="text-gray-300 mb-4">
        You don't have administrator permissions required to access this page.
      </p>
      <div className="space-y-2 text-sm text-gray-400 mb-6">
        <p>Current user: <span className="text-white font-mono">{userEmail}</span></p>
        <p>Role: <span className="text-yellow-300">{userRole || 'Not defined'}</span></p>
        <p>Admin status: <span className={isAdmin ? 'text-green-300' : 'text-red-300'}>{isAdmin ? 'Yes' : 'No'}</span></p>
      </div>
      <button
        onClick={() => window.location.href = '#/dashboard'}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
      >
        Back to Dashboard
      </button>
    </div>
  </div>
);

const ProtectedRoute = ({ children, requireAdmin = false, adminOnly = false, requireSubscription = false }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, profile, loading: subLoading } = useSubscription();
  const location = useLocation();

  const needsAdmin = requireAdmin || adminOnly;
  const isLoading = authLoading || subLoading;

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (needsAdmin && !isAdmin) {
    // Admin status is derived only after the profile finishes loading. Until
    // the profile is resolved, keep showing the loader instead of briefly
    // flashing "Access Denied" for a legitimate admin on a cold load.
    if (!profile) {
      return <FullScreenLoader />;
    }
    return <AdminDeniedScreen
      userEmail={user.email}
      userRole={profile?.role}
      isAdmin={isAdmin}
    />;
  }

  // Check subscription for protected features.
  // Priority: Lifetime > Active Paid > Active Trial > Free tier (default).
  // The Free tier is a valid plan with its own quota (5 analyses/month)
  // enforced at the analysis call (increment_analysis_usage RPC), NOT at page
  // entry. Previously this gate redirected ALL Free users to /choose-plan,
  // which is exactly why ~45/104 signups never ran a single analysis (cul-de-sac).
  if (requireSubscription && !isAdmin) {
    const isLifetime = profile?.billing_type === 'lifetime';
    const hasActivePaid = profile?.billing_status === 'active' && profile?.subscription_plan_id;
    const hasActiveTrial = profile?.is_in_trial && profile?.trial_ends_at && new Date(profile.trial_ends_at) > new Date();
    // Any authenticated, active user (Free or otherwise) is allowed in.
    // is_active === false means the account was explicitly disabled.
    const hasActiveProfile = !!profile && profile?.is_active !== false;

    const hasAccess = isLifetime || hasActivePaid || hasActiveTrial || hasActiveProfile;

    if (!hasAccess) {
      // Only block users whose account was explicitly deactivated, or who have no profile.
      return <Navigate to="/choose-plan" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
